/*
Briques partagées des écrans « réglages » (compte utilisateur, espace entreprise) :
cartes de section, champs à libellé flottant, badges, cartes-radio, barre de
sauvegarde ancrée, puces, alertes, et le hook de copy fr/en/es avec repli i18n.
*/
import { Input } from '@/core/dynamic';
import Loading from '@/core/loading';
import { useTranslation } from '@/helpers/translate';

export const CARD_CLASS = 'rounded-2xl border border-was-ink/8 bg-white shadow-[0_1px_2px_rgba(19,14,27,0.04),0_10px_24px_-14px_rgba(19,14,27,0.18)]';
export const LABEL_CLASS = 'absolute left-2 top-2.5 z-5 origin-0 cursor-text rounded-md bg-white px-1.5 py-0.5 text-[15px] text-was-muted duration-200';
export const INPUT_CLASS = 'block w-full rounded-lg border border-was-ink/12 bg-white px-3.5 py-3 text-[15px] text-was-ink outline-none transition-colors placeholder:text-was-muted hover:border-was-ink/25 focus:border-was-violet focus:ring-2 focus:ring-was-violet/20';
export const BTN_PRIMARY = 'inline-flex items-center justify-center gap-2 rounded-lg bg-was-ink px-5 py-2.5 text-[15px] font-normal text-white transition-colors duration-200 hover:bg-was-violet disabled:cursor-not-allowed disabled:opacity-50';
export const BTN_SECONDARY = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-was-ink/15 px-3 py-1.5 text-sm text-was-ink transition-colors duration-200 hover:border-was-ink/40 disabled:cursor-not-allowed disabled:opacity-50';
export const BTN_DANGER = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-red/30 px-3 py-1.5 text-sm text-red transition-colors duration-200 hover:bg-red-light disabled:opacity-50';
export const BTN_DANGER_SOLID = 'inline-flex items-center justify-center gap-2 rounded-lg bg-red px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-red-dark disabled:opacity-50';
export const LINK_ACTION = 'text-[14px] text-was-slate underline decoration-was-ink/25 underline-offset-4 transition-colors hover:text-was-ink hover:decoration-was-ink';
export const TEXT_BTN = 'px-3 py-2 text-[14px] text-was-slate transition-colors hover:text-was-ink disabled:opacity-50';

export function Card({
  id, title, desc, aside, children, className = '', padded = true,
}) {
  return (
    <section id={id} className={`scroll-mt-28 ${CARD_CLASS} ${padded ? 'p-5 sm:p-6 lg:p-8' : ''} ${className}`}>
      {title && (
        <header className={`flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6 ${padded ? 'mb-6' : 'p-5 pb-0 sm:p-6 sm:pb-0 lg:p-8 lg:pb-0'}`}>
          <div className="min-w-0">
            <h2 className="m-0 text-[17px] font-bold leading-snug tracking-[-0.01em] text-was-ink">{title}</h2>
            {desc && <p className="m-0 mt-1 text-[14px] leading-relaxed text-was-slate">{desc}</p>}
          </div>
          {aside && <div className="shrink-0">{aside}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function SubSection({
  title, desc, children, className = '',
}) {
  return (
    <div className={className}>
      {title && <h3 className="m-0 text-[15px] font-bold tracking-[-0.01em] text-was-ink">{title}</h3>}
      {desc && <p className="m-0 mt-1 text-[13px] leading-relaxed text-was-slate">{desc}</p>}
      <div className={title || desc ? 'mt-3' : ''}>{children}</div>
    </div>
  );
}

// Champ à libellé flottant (react-hook-form `register`).
export function Field({
  name, register, label, defaultValue, type = 'text', className = '', ...rest
}) {
  return (
    <div className={`relative ${className}`}>
      <Input register={() => register(name)}>
        <input type={type} defaultValue={defaultValue} {...rest} />
        <label className={LABEL_CLASS}>{label}</label>
      </Input>
    </div>
  );
}

// Variante contrôlée (value / onChange) pour les formulaires sans react-hook-form.
export function TextField({
  label, value, onChange, name, type = 'text', multiline = false, className = '', ...rest
}) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div className={`relative ${className}`}>
      <Tag
        name={name}
        id={`field-${name}`}
        type={multiline ? undefined : type}
        placeholder=" "
        value={value ?? ''}
        onChange={onChange}
        className={`${INPUT_CLASS} ${multiline ? 'min-h-[96px] resize-y' : ''}`}
        {...rest}
      />
      <label htmlFor={`field-${name}`} className={LABEL_CLASS}>{label}</label>
    </div>
  );
}

export function FloatingLabel({ children }) {
  return (
    <span className="pointer-events-none absolute -top-2 left-3 z-10 rounded-md bg-white px-1.5 text-[11px] text-was-muted">
      {children}
    </span>
  );
}

export function Badge({ children, tone = 'purple' }) {
  const tones = {
    purple: 'bg-was-violet/10 text-was-violet',
    dark: 'bg-was-ink/5 text-was-slate',
    green: 'bg-green-light text-green-dark',
    yellow: 'bg-yellow-light text-[#9a7400]',
    red: 'bg-red-light text-red',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone] || tones.purple}`}>
      {children}
    </span>
  );
}

export function ChoiceCard({
  checked, onClick, title, desc, disabled = false,
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition disabled:opacity-60 ${
        checked
          ? 'border-was-violet bg-was-violet/5 ring-1 ring-was-violet'
          : 'border-was-ink/12 bg-white hover:border-was-ink/25'
      }`}
    >
      <span className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border ${checked ? 'border-was-violet bg-was-violet' : 'border-was-ink/20 bg-white'}`}>
        {checked && <span className="size-2 rounded-full bg-white" />}
      </span>
      <span className="min-w-0">
        <span className="block text-[15px] font-medium text-was-ink">{title}</span>
        {desc && <span className="mt-0.5 block text-sm text-was-slate">{desc}</span>}
      </span>
    </button>
  );
}

// Puce supprimable (e-mails de facturation, domaines autorisés…).
export function Chip({ children, onRemove, removeLabel = 'remove' }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-was-violet/10 py-1 pl-3 pr-1.5 text-sm text-was-violet">
      <span className="truncate">{children}</span>
      {onRemove && (
        <button type="button" aria-label={removeLabel} onClick={onRemove} className="flex size-5 items-center justify-center rounded-full text-was-violet/70 transition-colors hover:bg-was-violet/15 hover:text-was-violet">
          <svg viewBox="0 0 20 20" fill="currentColor" className="size-3" aria-hidden="true"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" /></svg>
        </button>
      )}
    </span>
  );
}

export function Alert({ tone = 'error', children, className = '' }) {
  const tones = {
    error: 'border-red/20 bg-red-light text-red',
    success: 'border-green/20 bg-green-light text-green-dark',
    info: 'border-was-ink/10 bg-was-cream text-was-slate',
  };
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${tones[tone] || tones.error} ${className}`}>
      {children}
    </div>
  );
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-was-ink/15 px-4 py-10 text-center">
      <p className="m-0 text-[15px] font-medium text-was-ink">{title}</p>
      {hint && <p className="m-0 text-[13px] text-was-slate">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

// Barre ancrée en bas : n'apparaît qu'avec des modifications non enregistrées.
export function SaveBar({
  visible, state, onCancel, onSave, labels, railClass = '',
}) {
  const saving = state === 'saving';
  const saved = state === 'saved';
  const error = state === 'error';

  let message = labels.unsaved;
  let dot = 'bg-yellow';
  if (saving) message = labels.saving;
  if (saved) {
    message = labels.saved;
    dot = 'bg-green';
  }
  if (error) {
    message = labels.error;
    dot = 'bg-red';
  }

  return (
    <div
      aria-live="polite"
      className={`fixed inset-x-0 bottom-0 z-40 px-4 pb-4 transition-all duration-300 sm:pb-6 ${
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'
      }`}
    >
      <div className={`${railClass} flex items-center justify-between gap-4 rounded-2xl border border-was-ink/8 bg-white p-2 pl-4 shadow-[0_12px_40px_-12px_rgba(19,14,27,0.35)] lg:pl-6`}>
        <div className="flex min-w-0 items-center gap-2.5 text-[14px]">
          <span className={`size-2 shrink-0 rounded-full ${dot}`} />
          <span className={`truncate ${error ? 'text-red' : 'text-was-ink'}`}>{message}</span>
        </div>
        {!saved && (
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" className={TEXT_BTN} onClick={onCancel} disabled={saving}>{labels.cancel}</button>
            <button type="button" className={`min-w-[8.5rem] ${BTN_PRIMARY}`} onClick={onSave} disabled={saving}>
              {saving ? <Loading color="text-white" /> : labels.save}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Efface l'état « enregistré » de la barre après un court délai.
export const SAVED_STATE_MS = 2500;

const dig = (obj, path) => path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

/*
Fabrique le hook de copy d'un écran : `tx(path, legacyKey)` renvoie la clé i18n
existante si elle est traduite, sinon `<prefix>.<path>` si l'équipe l'a ajoutée à
l'API, sinon le dictionnaire local fr/en/es.
*/
export function createCopyHook(COPY, prefix) {
  return function useCopy() {
    const { t, lang } = useTranslation();
    const dict = COPY[lang] || COPY.fr;
    const tx = (path, legacyKey, values) => {
      if (legacyKey) {
        const legacy = t(legacyKey, values);
        if (legacy && legacy !== legacyKey && typeof legacy === 'string') return legacy;
      }
      const apiKey = `${prefix}.${path}`;
      const fromApi = t(apiKey, values);
      if (fromApi && fromApi !== apiKey && typeof fromApi === 'string') return fromApi;
      let local = dig(dict, path) ?? path;
      if (values && typeof local === 'string') {
        Object.entries(values).forEach(([k, v]) => { local = local.replaceAll(`{${k}}`, v); });
      }
      return local;
    };
    return { tx, t, lang };
  };
}
